import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldLogo } from "../components/landing/Logo";
import { 
  Users, Bell, Volume2, Watch, Smartphone, Headphones,
  AlertTriangle, CheckCircle2, Clock, Send, Plus, Trash2,
  Home, RefreshCw, Loader2, Mic, Phone, TrendingUp, CheckCheck
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocket } from "../hooks/useWebSocket";
import { useOfflineQueue } from "../hooks/useOfflineQueue";
import { ConnectionBanner } from "../components/ConnectionBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESTAURANT_ID = "default";

// Station colors
const STATION_COLORS = {
  boh: "bg-red-500/20 text-red-400 border-red-500/30",
  foh: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  bar: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  dish_pit: "bg-green-500/20 text-green-400 border-green-500/30",
  all: "bg-gold/20 text-gold border-gold/30"
};

// Device type icons
const DEVICE_ICONS = {
  watch: Watch,
  phone: Smartphone,
  earbuds: Headphones
};

// Device Card Component
const DeviceCard = ({ device, onRemove }) => {
  const Icon = DEVICE_ICONS[device.device_type] || Smartphone;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-card rounded-lg border border-gold/20 p-4"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-gold" />
          </div>
          <div>
            <h4 className="font-bold text-white text-sm">{device.staff_name}</h4>
            <p className="text-xs text-slate-400">{device.device_type} • {device.notification_mode}</p>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${STATION_COLORS[device.station] || STATION_COLORS.all}`}>
          {device.station}
        </span>
      </div>
    </motion.div>
  );
};

// High Tension Call Card - Special styling for receptionist escalations
const HighTensionCard = ({ alert, onPlayAudio }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const agitationScore = alert.metadata?.agitation_score || 0;
  const guestName = alert.metadata?.guest_name || "Unknown Guest";
  const intent = alert.metadata?.intent || "general";
  
  const handlePlay = async () => {
    if (alert.whisper_audio) {
      setIsPlaying(true);
      await onPlayAudio(alert.whisper_audio);
      setIsPlaying(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      className="rounded-lg border-2 border-red-500/50 bg-red-500/10 p-4 relative overflow-hidden"
    >
      {/* Pulsing indicator for critical */}
      {agitationScore >= 0.9 && (
        <div className="absolute top-2 right-2">
          <span className="flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
          <Phone className="w-5 h-5 text-red-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-500 text-white">
              HIGH TENSION
            </span>
            <span className="text-xs text-slate-400">{intent}</span>
          </div>
          <p className="text-sm text-white font-medium">{guestName}</p>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-red-400" />
              <span className="text-xs text-red-400 font-bold">{Math.round(agitationScore * 100)}% Agitation</span>
            </div>
            <span className="text-[10px] text-slate-500">
              {new Date(alert.timestamp).toLocaleTimeString()}
            </span>
          </div>
          {alert.metadata?.summary && (
            <p className="text-xs text-slate-400 mt-2 italic">"{alert.metadata.summary}"</p>
          )}
        </div>
        {alert.whisper_audio && (
          <button
            onClick={handlePlay}
            disabled={isPlaying}
            className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors"
            title="Play whisper audio"
          >
            {isPlaying ? (
              <Loader2 className="w-4 h-4 animate-spin text-red-400" />
            ) : (
              <Volume2 className="w-4 h-4 text-red-400" />
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Alert Card Component - handles both staff alerts and vision alerts
const AlertCard = ({ alert, onPlayAudio }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Map vision alert fields to our format
  const message = alert.message || alert.description || alert.title || "Alert";
  const priority = alert.priority || (alert.severity === "CRITICAL" ? "high" : alert.severity === "WARNING" ? "medium" : "low");
  const alertType = alert.alert_type || alert.category || "general";
  const targetStations = alert.target_stations || ["all"];
  
  const handlePlay = async () => {
    if (alert.whisper_audio) {
      setIsPlaying(true);
      await onPlayAudio(alert.whisper_audio);
      setIsPlaying(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`rounded-lg border p-4 ${
        priority === "high" 
          ? "bg-red-500/10 border-red-500/30" 
          : priority === "medium"
          ? "bg-amber-500/10 border-amber-500/30"
          : "bg-blue-500/10 border-blue-500/30"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
              priority === "high" ? "bg-red-500 text-white" :
              priority === "medium" ? "bg-amber-500 text-black" :
              "bg-blue-500 text-white"
            }`}>
              {priority}
            </span>
            <span className="text-xs text-slate-500">{alertType}</span>
          </div>
          <p className="text-sm text-white">{message}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-slate-500">
              {new Date(alert.timestamp).toLocaleTimeString()}
            </span>
            <span className="text-[10px] text-slate-600">→</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATION_COLORS[targetStations?.[0]] || STATION_COLORS.all}`}>
              {targetStations?.join(", ") || "all"}
            </span>
          </div>
        </div>
        {alert.whisper_audio && (
          <button
            onClick={handlePlay}
            disabled={isPlaying}
            className="ml-3 p-2 rounded-lg bg-gold/20 hover:bg-gold/30 transition-colors"
            title="Play whisper audio"
          >
            {isPlaying ? (
              <Loader2 className="w-4 h-4 animate-spin text-gold" />
            ) : (
              <Volume2 className="w-4 h-4 text-gold" />
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Main Staff Sync Dashboard
export default function StaffSyncDashboard() {
  const { token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [highTensionAlerts, setHighTensionAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isBroadcasting, setIsBroadcasting] = useState(false);

  // ── Live task feed from Iris ─────────────────────────────────────
  const [liveTasks, setLiveTasks] = useState([]);
  const [completingTask, setCompletingTask] = useState(null);

  // ── WebSocket ────────────────────────────────────────────────────
  const { status: wsStatus, lastEvent } = useWebSocket(RESTAURANT_ID);
  const isOnline = wsStatus === "connected";

  // ── Offline queue ────────────────────────────────────────────────
  const { enqueue, queueSize } = useOfflineQueue(isOnline, async (action) => {
    if (action.action === "complete_task") {
      await axios.post(
        `${API}/iris/tasks/${action.task_id}/complete`,
        { completed_by: action.completed_by },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
  });

  // New device form
  const [newDevice, setNewDevice] = useState({
    device_id: "", staff_name: "", device_type: "phone",
    notification_mode: "both", station: "foh"
  });
  
  // New alert form
  const [newAlert, setNewAlert] = useState({
    alert_type: "service", message: "", target_stations: ["all"],
    priority: "medium", include_whisper: true
  });
  
  const headers = { Authorization: `Bearer ${token}` };

  // ── Initial data fetch ────────────────────────────────────────────
  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    try {
      const [devicesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/staff/devices`, { headers }),
        axios.get(`${API}/staff/all-alerts?limit=30`, { headers })
      ]);
      setDevices(devicesRes.data.devices || []);
      const allAlerts = alertsRes.data.alerts || [];
      const tensionAlerts = allAlerts.filter(a => a.source === "receptionist_sentiment");
      const otherAlerts = allAlerts.filter(a => a.source !== "receptionist_sentiment");
      setHighTensionAlerts(tensionAlerts);
      setAlerts(otherAlerts.slice(0, 10));
      setAlertStats(alertsRes.data.stats || {});
    } catch (err) {
      if (showLoading) toast.error("Failed to load staff data");
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [token]);

  // ── Fetch live tasks ──────────────────────────────────────────────
  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/iris/tasks?restaurant_id=${RESTAURANT_ID}`, { headers });
      setLiveTasks(res.data.tasks || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
      fetchTasks();
      // Fallback poll at 30s (WebSocket is primary, this is the safety net)
      const poll = setInterval(() => {
        fetchData(false);
        fetchTasks();
      }, 30000);
      return () => clearInterval(poll);
    }
  }, [token, fetchData, fetchTasks]);

  // ── React to WebSocket events ─────────────────────────────────────
  useEffect(() => {
    if (!lastEvent) return;

    if (lastEvent.type === "iris_task") {
      // Prepend new task to live feed without re-fetching
      const newTask = {
        task_id: lastEvent.task_id,
        task_type: lastEvent.task_type,
        title: lastEvent.title,
        description: lastEvent.description,
        location: lastEvent.location,
        priority: lastEvent.priority,
        assigned_name: lastEvent.assigned_name,
        created_at: lastEvent.created_at,
        status: "pending",
      };
      setLiveTasks(prev => [newTask, ...prev].slice(0, 20));
      toast.info(`New task: ${lastEvent.title}`, { duration: 4000 });
    }

    // Echo escalation → high-priority alert in Atlas (appears instantly, no refresh)
    if (
      lastEvent.type === "supervisor_event" &&
      lastEvent.supervisor === "echo" &&
      (lastEvent.event_type === "escalation" || lastEvent.priority === "critical" || lastEvent.priority === "high")
    ) {
      const ctx = lastEvent.context || {};
      const newAlert = {
        alert_id:    `WS-ECHO-${Date.now()}`,
        alert_type:  "escalation",
        source:      "receptionist_sentiment",
        message:     `High Tension — ${lastEvent.action}`,
        priority:    lastEvent.priority === "critical" ? "high" : "medium",
        timestamp:   lastEvent.timestamp || new Date().toISOString(),
        metadata: {
          session_id:     ctx.session_id,
          agitation_score: ctx.agitation_score || 0.8,
          mood:           ctx.mood || "agitated",
          intent:         ctx.intent || "escalation",
          guest_name:     ctx.customer_name || "Guest",
          summary:        lastEvent.action,
        },
      };
      setHighTensionAlerts(prev => [newAlert, ...prev].slice(0, 10));
      toast.warning(
        `Echo Escalation: ${lastEvent.action?.slice(0, 60)}`,
        { duration: 6000 }
      );
    }

    if (lastEvent.type === "supervisor_event" && lastEvent.priority === "critical") {
      toast.warning(`${lastEvent.supervisor?.toUpperCase()}: ${lastEvent.action}`, { duration: 6000 });
      fetchData(false);
    }
  }, [lastEvent]);

  // ── Complete task (with offline queue) ───────────────────────────
  const handleCompleteTask = async (taskId) => {
    const completedBy = "staff"; // In production: pull from auth context
    setCompletingTask(taskId);

    if (isOnline) {
      try {
        await axios.post(
          `${API}/iris/tasks/${taskId}/complete`,
          { completed_by: completedBy },
          { headers }
        );
        setLiveTasks(prev => prev.filter(t => t.task_id !== taskId));
        toast.success("Task marked complete");
      } catch {
        toast.error("Failed — queued for retry");
        enqueue({ action: "complete_task", task_id: taskId, completed_by: completedBy });
        setLiveTasks(prev => prev.map(t =>
          t.task_id === taskId ? { ...t, status: "queued_offline" } : t
        ));
      }
    } else {
      enqueue({ action: "complete_task", task_id: taskId, completed_by: completedBy });
      setLiveTasks(prev => prev.map(t =>
        t.task_id === taskId ? { ...t, status: "queued_offline" } : t
      ));
      toast.info("Offline — completion queued. Will sync when reconnected.", { duration: 5000 });
    }
    setCompletingTask(null);
  };

  // ── Register device ──────────────────────────────────────────────
  const handleRegisterDevice = async () => {
    if (!newDevice.staff_name || !newDevice.device_id) {
      toast.error("Please fill in device ID and staff name");
      return;
    }
    try {
      await axios.post(`${API}/staff/register-device`, newDevice, { headers });
      toast.success(`Device registered for ${newDevice.staff_name}`);
      setNewDevice({ device_id: "", staff_name: "", device_type: "phone", notification_mode: "both", station: "foh" });
      fetchData();
    } catch {
      toast.error("Failed to register device");
    }
  };
  
  // ── Broadcast alert ──────────────────────────────────────────────
  const handleBroadcastAlert = async () => {
    if (!newAlert.message) { toast.error("Please enter an alert message"); return; }
    setIsBroadcasting(true);
    try {
      const res = await axios.post(`${API}/staff/broadcast-alert`, null, {
        headers,
        params: {
          alert_type: newAlert.alert_type, message: newAlert.message,
          target_stations: newAlert.target_stations, priority: newAlert.priority,
          include_whisper: newAlert.include_whisper
        }
      });
      toast.success(`Alert broadcasted to ${res.data.target_devices} devices`);
      setNewAlert({ ...newAlert, message: "" });
      if (res.data.whisper_audio) playAudio(res.data.whisper_audio);
    } catch { toast.error("Failed to broadcast alert"); }
    finally { setIsBroadcasting(false); }
  };
  
  // ── Audio helper ─────────────────────────────────────────────────
  const playAudio = (base64Audio) => {
    try {
      const blob = new Blob([Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0))], { type: "audio/mp3" });
      new Audio(URL.createObjectURL(blob)).play();
    } catch {}
  };

  const PRIORITY_STYLE = {
    critical: "bg-red-500 text-white",
    high:     "bg-orange-500 text-white",
    normal:   "bg-blue-500 text-white",
    low:      "bg-gray-500 text-white",
  };
  
  return (
    <div className="min-h-screen bg-[#040F49]" data-testid="staff-sync-dashboard">
      <div className="noise-overlay" />

      {/* ── Offline / Reconnecting banner ── */}
      <ConnectionBanner status={wsStatus} queueSize={queueSize} />
      
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <span className="font-inter text-sm font-bold text-gold tracking-wider">STAFF SYNC</span>
            {/* Live status dot */}
            <span
              data-testid="ws-status-indicator"
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{
                background: isOnline ? "rgba(0,255,65,0.1)" : "rgba(245,158,11,0.1)",
                border: `1px solid ${isOnline ? "rgba(0,255,65,0.3)" : "rgba(245,158,11,0.3)"}`,
                color: isOnline ? "#00FF41" : "#f59e0b",
              }}
            >
              <span className={`relative flex h-1.5 w-1.5 rounded-full ${isOnline ? "animate-ping" : ""}`}
                style={{ background: isOnline ? "#00FF41" : "#f59e0b" }} />
              {isOnline ? "LIVE" : wsStatus.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="text-slate-400 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </button>
            <a href="/admin/vision" className="text-slate-400 hover:text-gold text-xs">Vision</a>
            <a href="/admin/founding-50" className="text-slate-400 hover:text-gold text-xs">Dashboard</a>
            <a href="/" className="text-slate-400 hover:text-white"><Home className="w-5 h-5" /></a>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-card rounded-lg border border-gold/20 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Phone className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{alertStats.high_tension_calls || 0}</p>
              <p className="text-xs text-slate-400">High Tension Calls</p>
            </div>
          </div>
          <div className="glass-card rounded-lg border border-gold/20 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{alertStats.high_priority || 0}</p>
              <p className="text-xs text-slate-400">High Priority</p>
            </div>
          </div>
          <div className="glass-card rounded-lg border border-gold/20 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00FF41]/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#00FF41]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{liveTasks.filter(t => t.status === "pending").length}</p>
              <p className="text-xs text-slate-400">Live Tasks</p>
            </div>
          </div>
          <div className="glass-card rounded-lg border border-gold/20 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{devices.length}</p>
              <p className="text-xs text-slate-400">Active Devices</p>
            </div>
          </div>
        </div>

        {/* ── Live Task Feed from Iris ── */}
        {liveTasks.length > 0 && (
          <div className="glass-card rounded-xl border border-[#00FF41]/20 bg-[#00FF41]/02 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF41] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00FF41]" />
                </span>
                <h2 className="font-bold text-white text-sm">Live Task Feed</h2>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#00FF41]/10 text-[#00FF41] border border-[#00FF41]/30">
                  {liveTasks.filter(t => t.status === "pending").length} PENDING
                </span>
              </div>
              <span className="text-xs text-slate-500">Routed by Iris — updates instantly</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3 max-h-72 overflow-y-auto">
              {liveTasks.map((task) => (
                <motion.div
                  key={task.task_id}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-lg border p-3 flex items-start gap-3 ${
                    task.status === "queued_offline"
                      ? "bg-amber-500/10 border-amber-500/30"
                      : "bg-white/3 border-white/10"
                  }`}
                >
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold shrink-0 ${PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.normal}`}>
                    {task.priority?.toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{task.title}</p>
                    <p className="text-slate-400 text-[11px] truncate mt-0.5">{task.description}</p>
                    {task.location && (
                      <span className="text-[10px] text-slate-500 mt-1 block">{task.location}</span>
                    )}
                    {task.status === "queued_offline" && (
                      <span className="text-[10px] text-amber-400 mt-1 block">Queued offline — will sync</span>
                    )}
                  </div>
                  {task.status !== "queued_offline" && (
                    <button
                      data-testid={`complete-task-${task.task_id}`}
                      onClick={() => handleCompleteTask(task.task_id)}
                      disabled={completingTask === task.task_id}
                      className="shrink-0 p-1.5 rounded-lg bg-[#00FF41]/10 hover:bg-[#00FF41]/20 border border-[#00FF41]/20 text-[#00FF41] transition-colors"
                      title="Mark complete"
                    >
                      {completingTask === task.task_id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <CheckCheck className="w-3.5 h-3.5" />
                      }
                    </button>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* High Tension Calls Panel */}
        {highTensionAlerts.length > 0 && (
          <div className="glass-card rounded-xl border-2 border-red-500/30 bg-red-500/5 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Phone className="w-5 h-5 text-red-400" />
                  <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                  </span>
                </div>
                <h2 className="font-inter font-bold text-red-400">HIGH TENSION CALLS</h2>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">
                  {highTensionAlerts.length} ACTIVE
                </span>
              </div>
              <p className="text-xs text-slate-400">Auto-escalated from AI Receptionist</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 max-h-[300px] overflow-y-auto">
              {highTensionAlerts.map((alert, idx) => (
                <HighTensionCard key={idx} alert={alert} onPlayAudio={playAudio} />
              ))}
            </div>
          </div>
        )}
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Broadcast Alert Panel */}
          <div className="lg:col-span-2">
            <div className="glass-card rounded-xl border border-gold/20 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Bell className="w-5 h-5 text-gold" />
                <h2 className="font-inter font-bold text-white">Broadcast Alert</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-slate-400 text-xs">Alert Type</Label>
                  <Select value={newAlert.alert_type} onValueChange={v => setNewAlert({...newAlert, alert_type: v})}>
                    <SelectTrigger className="mt-1 bg-indigo-700/50 border-gold/20 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="safety">Safety</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="inventory">Inventory</SelectItem>
                      <SelectItem value="quality">Quality</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Priority</Label>
                  <Select value={newAlert.priority} onValueChange={v => setNewAlert({...newAlert, priority: v})}>
                    <SelectTrigger className="mt-1 bg-indigo-700/50 border-gold/20 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High (Urgent)</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Target Station</Label>
                  <Select value={newAlert.target_stations[0]} onValueChange={v => setNewAlert({...newAlert, target_stations: [v]})}>
                    <SelectTrigger className="mt-1 bg-indigo-700/50 border-gold/20 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stations</SelectItem>
                      <SelectItem value="boh">BOH (Kitchen)</SelectItem>
                      <SelectItem value="foh">FOH (Front)</SelectItem>
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="dish_pit">Dish Pit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={newAlert.include_whisper}
                      onChange={e => setNewAlert({...newAlert, include_whisper: e.target.checked})}
                      className="w-4 h-4 rounded border-gold/30" />
                    <span className="text-sm text-slate-300 flex items-center gap-1">
                      <Volume2 className="w-3 h-3" /> Include Whisper Audio
                    </span>
                  </label>
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-slate-400 text-xs">Alert Message</Label>
                <Input value={newAlert.message} onChange={e => setNewAlert({...newAlert, message: e.target.value})}
                  placeholder="e.g., Table 5 needs attention" className="mt-1 bg-indigo-700/50 border-gold/20 text-white" />
              </div>
              <Button onClick={handleBroadcastAlert} disabled={isBroadcasting || !newAlert.message}
                className="w-full bg-gold hover:bg-gold/80 text-indigo-700 font-bold">
                {isBroadcasting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Broadcast to Staff
              </Button>
            </div>
            
            {/* Recent Alerts */}
            <div className="glass-card rounded-xl border border-gold/20 p-6">
              <h3 className="font-inter font-bold text-white mb-4">Recent Alerts</h3>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {alerts.length === 0
                  ? <p className="text-sm text-slate-500 text-center py-8">No recent alerts</p>
                  : alerts.map((alert, idx) => (
                    <AlertCard key={idx} alert={alert} onPlayAudio={playAudio} />
                  ))}
              </div>
            </div>
          </div>
          
          {/* Devices Panel */}
          <div>
            <div className="glass-card rounded-xl border border-gold/20 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-5 h-5 text-gold" />
                <h2 className="font-inter font-bold text-white">Register Device</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-400 text-xs">Device ID</Label>
                  <Input value={newDevice.device_id} onChange={e => setNewDevice({...newDevice, device_id: e.target.value})}
                    placeholder="e.g., iphone-john-001" className="mt-1 bg-indigo-700/50 border-gold/20 text-white text-sm" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Staff Name</Label>
                  <Input value={newDevice.staff_name} onChange={e => setNewDevice({...newDevice, staff_name: e.target.value})}
                    placeholder="e.g., John Smith" className="mt-1 bg-indigo-700/50 border-gold/20 text-white text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-slate-400 text-xs">Device</Label>
                    <Select value={newDevice.device_type} onValueChange={v => setNewDevice({...newDevice, device_type: v})}>
                      <SelectTrigger className="mt-1 bg-indigo-700/50 border-gold/20 text-white text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="watch">Watch</SelectItem>
                        <SelectItem value="earbuds">Earbuds</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Station</Label>
                    <Select value={newDevice.station} onValueChange={v => setNewDevice({...newDevice, station: v})}>
                      <SelectTrigger className="mt-1 bg-indigo-700/50 border-gold/20 text-white text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boh">BOH</SelectItem>
                        <SelectItem value="foh">FOH</SelectItem>
                        <SelectItem value="bar">Bar</SelectItem>
                        <SelectItem value="dish_pit">Dish Pit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleRegisterDevice} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white">
                  <Plus className="w-4 h-4 mr-2" /> Register Device
                </Button>
              </div>
            </div>
            
            {/* Registered Devices */}
            <div className="glass-card rounded-xl border border-gold/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-inter font-bold text-white">Registered Devices</h3>
                <span className="text-xs text-slate-500">{devices.length} active</span>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {devices.length === 0
                  ? <p className="text-sm text-slate-500 text-center py-8">No devices registered</p>
                  : devices.map((device, idx) => <DeviceCard key={idx} device={device} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
